import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { min, max, get } from 'lodash';
import { useAvailableToBillSearchContext } from '~/modules/billing-invoicing/common/components/AvailableToBillSubTab/AvailableToBillSearchContext';
import { itemTypes } from '~/modules/common/components/SearchBox/Facets/BillingItemTypeFacet/enhancers/useBillingItemType';
import { dateTag } from '~/modules/common/components/SearchBox/Facets/DateRangeFacet/tag';
import { projectTag } from '~/modules/common/components/SearchBox/Facets/ProjectFacet/tag';
import { projectManagerTag } from '~/modules/common/components/SearchBox/Facets/ProjectManagerFacet/tag';
import { getDateForCriteria } from '~/modules/common/util';
import {
  dateToMidnightUTCObject,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';

const formatDate = date => date?.toLocaleString(DateTime.DATE_MED);

const getDateFilter = (startDate, endDate) => {
  if (!startDate && !endDate) return null;

  return {
    range: { startDate, endDate },
    key:
      startDate && endDate
        ? `(${formatDate(startDate)} - ${formatDate(endDate)})`
        : startDate
        ? `(${formatDate(startDate)} -`
        : `- ${formatDate(endDate)})`
  };
};

const formatProjectFilter = (projectUri, projectName) => ({
  value: projectUri,
  key: projectName,
  displayText: projectName,
  label: projectName
});

export const useCreateBillingItemFilterFromAvailableToBillFilter = ({
  availableToBillProject,
  availableToBillEndDate,
  availableToBillStartDate,
  availableToBillItemType,
  projectUri,
  projectName
}) => {
  const intl = useIntl();

  const availableToBillSearchContext = useAvailableToBillSearchContext();

  const { criterions } = availableToBillSearchContext?.searchCriteria || {};

  const range = getDateForCriteria(criterions, dateTag);

  const startDate = max(
    [
      typeof range?.startDate === 'string'
        ? dateToMidnightUTCObject(range?.startDate)
        : range?.startDate,
      availableToBillStartDate &&
        mapRepliconDateToUtcObject(availableToBillStartDate)
    ].filter(Boolean)
  );

  const endDate = min(
    [
      typeof range?.endDate === 'string'
        ? dateToMidnightUTCObject(range?.endDate)
        : range?.endDate,
      availableToBillEndDate &&
        mapRepliconDateToUtcObject(availableToBillEndDate)
    ].filter(Boolean)
  );

  if (availableToBillProject || (projectUri && projectName)) {
    return {
      keywords: [],
      criterions: {
        [dateTag]: [getDateFilter(startDate, endDate)].filter(Boolean),
        [projectTag]: [
          availableToBillProject
            ? formatProjectFilter(
                availableToBillProject.id,
                availableToBillProject.displayText
              )
            : formatProjectFilter(projectUri, projectName)
        ],
        'itemType:': availableToBillItemType
          ? [
              {
                value: availableToBillItemType,
                key: itemTypes(intl).find(
                  itemType => itemType.id === availableToBillItemType
                ).label
              }
            ]
          : []
      }
    };
  }

  return {
    keywords: [],
    criterions: {
      'date:': [getDateFilter(startDate, endDate)].filter(Boolean),
      [projectTag]: get(criterions, projectTag, []),
      [projectManagerTag]: get(criterions, projectManagerTag, [])
    }
  };
};
